/**
 * Role Switcher Component
 * 
 * Allows users to view the application as a different role for testing purposes.
 */

import React from 'react';
import { Select, Button, Badge } from '../UI';
import { useRoleSwitch } from '../../hooks/useRoleSwitch';
import { usePermissions } from '../../hooks/usePermissions';
import { useQuery } from '@tanstack/react-query';
import { listRoles, type Role } from '../../api/rbac';
import { useWorkspace } from '../../hooks/useWorkspace';
import { User, X } from 'lucide-react';

interface RoleSwitcherProps {
  className?: string;
}

export const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ className }) => {
  const { tenantId, projectId } = useWorkspace();
  const { mockRoleId, setMockRole, clearMockRole, isMockRoleActive } = useRoleSwitch();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  
  // Only show RoleSwitcher to users with admin_tech role
  const canViewRoleSwitcher = hasPermission('keystone_rfp_admin_tech') || 
                               hasPermission('admin_tech') ||
                               hasPermission('keystone_proposal_admin_tech');
  
  // Don't render if user doesn't have permission
  if (!isLoadingPermissions && !canViewRoleSwitcher) {
    return null;
  }

  // Fetch available roles
  const { data: roles, isLoading } = useQuery<Role[]>({
    queryKey: ['roles', tenantId, projectId],
    queryFn: () => listRoles({ tenant_id: tenantId, project_id: projectId }),
    enabled: !!tenantId || !!projectId,
  });

  // Get current mock role details
  const currentMockRole = React.useMemo(() => {
    if (!mockRoleId || !roles) return null;
    return roles.find(r => r.id === mockRoleId);
  }, [mockRoleId, roles]);

  const handleRoleChange = (roleId: string) => {
    if (roleId === '') {
      clearMockRole();
    } else {
      setMockRole(roleId);
    }
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="text-sm text-gray-500">Loading roles...</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-theme-text flex items-center gap-1">
          <User className="w-4 h-4" />
          View As:
        </label>
        <Select
          value={mockRoleId || ''}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleRoleChange(e.target.value)}
          className="min-w-[200px]"
        >
          <option value="">My Actual Role</option>
          {roles?.map((role) => (
            <option key={role.id} value={role.id}>
              {role.display_name || role.name}
            </option>
          ))}
        </Select>
        {isMockRoleActive && currentMockRole && (
          <Badge variant="warning" className="text-xs">
            Mock: {currentMockRole.display_name || currentMockRole.name}
          </Badge>
        )}
        {isMockRoleActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearMockRole}
            className="text-xs"
          >
            <X className="w-3 h-3 mr-1" />
            Exit
          </Button>
        )}
      </div>
    </div>
  );
};

